import { onBeforeUnmount, ref } from 'vue';
import { buildDeploymentEventsUrl } from '@/services/wpDeploymentService';
import { resolveAuthHeaderValue } from '@/utils/authToken';

const keyForEvent = payload => {
  const type = String(payload?.type || '');
  const timestamp = String(payload?.timestamp || '');
  const step = String(payload?.step || '');
  const stream = String(payload?.stream || '');
  const message = String(payload?.message || '');
  return `${type}|${timestamp}|${step}|${stream}|${message}`;
};

const parseEventPayload = (eventType, eventData) => {
  try {
    const parsed = JSON.parse(eventData || '{}');
    if (!parsed.type) parsed.type = eventType;
    return parsed;
  } catch {
    return {
      type: eventType,
      message: String(eventData || ''),
      timestamp: new Date().toISOString(),
    };
  }
};

export function useDeploymentEvents() {
  const connectionStatus = ref('disconnected');
  const lastError = ref('');

  let streamAbortController = null;
  let reconnectTimer = null;
  let reconnectAttempts = 0;
  let activeDeploymentId = '';
  let handlers = {};
  const seenKeys = new Set();
  const seenOrder = [];

  const clearReconnectTimer = () => {
    if (!reconnectTimer) return;
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  };

  const clearSeenCache = () => {
    seenKeys.clear();
    seenOrder.splice(0, seenOrder.length);
  };

  const close = () => {
    clearReconnectTimer();
    if (streamAbortController) {
      streamAbortController.abort();
      streamAbortController = null;
    }
    activeDeploymentId = '';
    handlers = {};
    connectionStatus.value = 'disconnected';
  };

  const isDuplicate = payload => {
    const key = keyForEvent(payload);
    if (!key || key === '||||') return false;
    if (seenKeys.has(key)) return true;
    seenKeys.add(key);
    seenOrder.push(key);
    if (seenOrder.length > 4000) {
      const old = seenOrder.shift();
      if (old) seenKeys.delete(old);
    }
    return false;
  };

  const scheduleReconnect = () => {
    if (!activeDeploymentId) return;
    clearReconnectTimer();
    reconnectAttempts += 1;
    connectionStatus.value = 'reconnecting';
    const delayMs = Math.min(6000, 800 * reconnectAttempts);
    reconnectTimer = setTimeout(() => {
      open(activeDeploymentId, handlers, { resetSeenCache: false });
    }, delayMs);
  };

  const dispatchEvent = payload => {
    if (isDuplicate(payload)) return;
    const type = String(payload?.type || '').toLowerCase();

    if (handlers?.onEvent) handlers.onEvent(payload);

    if (type === 'heartbeat') {
      if (handlers?.onHeartbeat) handlers.onHeartbeat(payload);
      return;
    }

    if (handlers?.onNamedEvent) handlers.onNamedEvent(type, payload);
  };

  const processSseLine = (line, state) => {
    if (line === '') {
      if (state.dataLines.length === 0) {
        state.eventName = '';
        return;
      }
      const eventType = String(state.eventName || 'message').toLowerCase();
      const payload = parseEventPayload(eventType, state.dataLines.join('\n'));
      dispatchEvent(payload);
      state.eventName = '';
      state.dataLines = [];
      return;
    }

    if (line.startsWith(':')) return;

    const separatorIndex = line.indexOf(':');
    const field = separatorIndex < 0 ? line : line.slice(0, separatorIndex);
    let value = separatorIndex < 0 ? '' : line.slice(separatorIndex + 1);
    if (value.startsWith(' ')) value = value.slice(1);

    if (field === 'event') {
      state.eventName = value;
      return;
    }
    if (field === 'data') {
      state.dataLines.push(value);
    }
  };

  const openStreamWithFetch = async deploymentId => {
    const url = buildDeploymentEventsUrl(deploymentId);

    const controller = new AbortController();
    streamAbortController = controller;

    try {
      const authHeader = resolveAuthHeaderValue();
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          ...(authHeader ? { Authorization: authHeader } : {}),
        },
        cache: 'no-store',
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`SSE request failed with status ${response.status}`);
      }
      if (!response.body) {
        throw new Error('SSE stream has no body');
      }

      reconnectAttempts = 0;
      connectionStatus.value = 'connected';
      if (handlers?.onOpen) handlers.onOpen();

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      const parserState = { buffer: '', eventName: '', dataLines: [] };

      let isDone = false;
      while (!isDone) {
        const { value, done } = await reader.read();
        isDone = done;
        if (isDone) break;

        parserState.buffer += decoder.decode(value, { stream: true });
        let newlineIndex = parserState.buffer.indexOf('\n');

        while (newlineIndex >= 0) {
          let line = parserState.buffer.slice(0, newlineIndex);
          parserState.buffer = parserState.buffer.slice(newlineIndex + 1);
          if (line.endsWith('\r')) line = line.slice(0, -1);
          processSseLine(line, parserState);
          newlineIndex = parserState.buffer.indexOf('\n');
        }
      }

      if (parserState.buffer) {
        let tailLine = parserState.buffer;
        if (tailLine.endsWith('\r')) tailLine = tailLine.slice(0, -1);
        processSseLine(tailLine, parserState);
        processSseLine('', parserState);
      }
    } finally {
      if (streamAbortController === controller) {
        streamAbortController = null;
      }
    }
  };

  const open = (deploymentId, nextHandlers = {}, options = {}) => {
    const normalizedId = String(deploymentId || '').trim();
    if (!normalizedId) return;

    activeDeploymentId = normalizedId;
    handlers = nextHandlers || {};
    lastError.value = '';
    clearReconnectTimer();

    if (options.resetSeenCache !== false) clearSeenCache();

    if (streamAbortController) {
      streamAbortController.abort();
      streamAbortController = null;
    }

    connectionStatus.value = reconnectAttempts > 0 ? 'reconnecting' : 'connecting';

    openStreamWithFetch(normalizedId)
      .then(() => {
        if (!activeDeploymentId) return;
        lastError.value = 'SSE connection lost';
        if (handlers?.onError) handlers.onError(lastError.value);
        scheduleReconnect();
      })
      .catch(error => {
        if (error?.name === 'AbortError') return;
        lastError.value = error?.message || 'Unable to open SSE stream';
        if (handlers?.onError) handlers.onError(lastError.value);
        scheduleReconnect();
      });
  };

  const reconnect = () => {
    if (!activeDeploymentId) return;
    reconnectAttempts = 0;
    open(activeDeploymentId, handlers, { resetSeenCache: false });
  };

  onBeforeUnmount(() => {
    close();
  });

  return {
    connectionStatus,
    lastError,
    open,
    close,
    reconnect,
  };
}
